<template>
  <div>
    <v-date-picker
      v-bind="$attrs"
      :popover="{ visibility: 'click', placement: 'bottom-end' }"
      :columns="$screens({ default: 1, lg: 2 })"
      mode="range"
      class="r-date-picker"
      @input="$emit('input', $event)"
      @dayclick="$emit('dayclick', $event)"
    >
      <div class="input">
        {{ formattedDateRange }}
        <CIcon name="faChevronDown" />
      </div>
    </v-date-picker>
  </div>
</template>

<script>
/**
 * Wrapper component for v-calendar's date-picker
 * component which uses the default slot to render
 * the datepicker as a button instead of the usual
 * date input box.
 *
 * Passes all props through to v-date-picker, see
 * v-calendar docs for the available options:
 *
 * https://docs.vcalendar.io/api/datepicker.html#props
 */
import { format, getDate, getMonth, getYear } from 'date-fns'

export default {
  name: 'RDateRangePicker',
  computed: {
    // Array of attribute objects for v-date-picker styling
    formattedDateRange () {
      const start = this.$attrs.value.start
      const end = this.$attrs.value.end

      const sameDay = getDate(start) === getDate(end)
      const sameMonth = getMonth(start) === getMonth(end)
      const sameYear = getYear(start) === getYear(end)

      const startDay = format(start, 'd')
      const startMonth = this.formatMonth(start)
      const startYear = format(start, 'yyyy')
      const endDay = format(end, 'd')
      const endMonth = this.formatMonth(end)
      const endYear = format(end, 'yyyy')

      if (sameYear && sameMonth && sameDay) {
        return `${startMonth} ${startDay}, ${startYear}`
      }
      if (sameYear && sameMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${startYear}`
      }
      if (sameYear) {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`
      }
      return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`
    }
  },
  methods: {
    /**
     * Format month name: use MMM (3 chars) if MMMM is >= 7 chars, otherwise use MMMM
     * @param {Date} date
     * @returns {String} formatted month
     */
    formatMonth (date) {
      const fullMonth = format(date, 'MMMM')
      const abbrevMonth = format(date, 'MMM')
      return fullMonth.length >= 7 ? abbrevMonth : fullMonth
    }
  }
}
</script>

<style lang="scss" scoped>
.r-date-picker {
  cursor: pointer;

  .input {
    font-weight: bold;
    font-size: 1.1rem;
  }
}
/* Custom Styles for Date Picker Component */
// Fonts and default colors
::v-deep .vc-container {
  font-family: 'Figtree', sans-serif;
  color: $secondary-dark;
}
::v-deep .vc-bg-blue-600 {
  background-color: $primary-base !important;
}

::v-deep .vc-bg-blue-200 {
  background-color: $primary-base !important;
  opacity: 0.4;
}

.vc-text-gray-900 {
  color: #2F343F;
}
::v-deep .vc-popover-content-wrapper[data-v-88de91da] {
  padding-left: 18px;
}
</style>
